/**
 * Customers.jsx — Tab 5
 *
 * Shows contacts whose computed _stage is 'customer' — via a converted trial,
 * stage_override = 'customer' set manually, or the Clerk customer merge
 * (backend lib/clerkRoster.js): product users with no lifecycle flag are
 * customers and are auto-flagged/auto-created as contacts.
 *
 * Table follows the Trial Management pattern (IMP-1806): clickable status-bar
 * quick filters, search, and per-column sort + spreadsheet-style filters in
 * the headers (ColumnFilter component).
 *
 * Props:
 *   data           — from useSheetData
 *   onOpenTimeline — (contact) => void
 *   getToken       — from useAuth
 *   onRefresh      — () => void
 *   amplitudeScores — { [email]: { score, tier, currentMonthMsgs, lastActiveMonth } }
 *   settings       — appSettings
 */

import { useMemo, useState, useEffect, useRef, Fragment } from 'react';
import { deleteTrials as apiDeleteTrials, updateContact as apiUpdateContact, mergeClerkContact, dismissMergeSuggestion, apiFlagClerkUser, offboardContact } from '../api/backend.js';
import { SCORE_TIER_COLOR, SCORE_TIER_BG, SCORE_TIER_LABEL } from '../utils/amplitude.js';
import { InlineDate } from '../components/InlineEdit.jsx';
import { trialFeatureCount } from '../utils/health.js';
import ColumnFilter, { fixedPopupPos } from '../components/ColumnFilter.jsx';
import MergeContactModal from '../components/MergeContactModal.jsx';
import UsageExportModal from '../components/UsageExportModal.jsx';
import UsageSummaryModal from '../components/UsageSummaryModal.jsx';
import { computeStage } from '../utils/stage.js';
import ContractsInvoices from './ContractsInvoices.jsx';
import UsageJournalPanel from '../components/UsageJournalPanel.jsx';

// ── Helpers ───────────────────────────────────────────────────────────────────

// BQ DATE/TIMESTAMP cells come back as { value: '...' } objects.
const _v = (x) => (x && typeof x === 'object' && 'value' in x) ? String(x.value ?? '') : String(x ?? '');

// Parse a date string in local time. Pure date strings ("YYYY-MM-DD") are
// treated as local midnight so they don't shift a day in UTC-west timezones
// (same helper Trial Management uses — new Date('2027-05-31') is UTC
// midnight, which renders as May 30 in US timezones).
function parseDateLocal(dateStr) {
  const s = String(dateStr ?? '');
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + 'T00:00:00');
  return new Date(s);
}

function fmt(dateStr) {
  const s = _v(dateStr);
  if (!s) return '—';
  const d = parseDateLocal(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysUntil(dateStr) {
  const s = _v(dateStr);
  if (!s) return null;
  const d = parseDateLocal(s);
  if (isNaN(d)) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

function daysSince(tsVal) {
  const raw = _v(tsVal);
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

function fmtDaysAgo(n) {
  if (n === null) return '—';
  if (n === 0) return 'Today';
  if (n === 1) return 'Yesterday';
  if (n < 7)  return `${n}d ago`;
  if (n < 30) return `${Math.floor(n / 7)}w ago`;
  if (n < 365) return `${Math.floor(n / 30)}mo ago`;
  return `${Math.floor(n / 365)}y ago`;
}

function renewalColor(days, redDays, amberDays) {
  if (days === null) return undefined;
  if (days < redDays)   return 'var(--c-red)';
  if (days < amberDays) return 'var(--c-amber)';
  return 'var(--c-green)';
}

function touchColor(days) {
  if (days === null) return 'var(--c-text-3)';
  if (days < 14)  return 'var(--c-green)';
  if (days < 45)  return 'var(--c-amber)';
  return 'var(--c-red)';
}

// Churn risk: combines renewal urgency + last touch gap + amplitude engagement
function churnRisk(renewalDays, touchDays, ampScore) {
  const noTouch = touchDays === null || touchDays > 60;
  const staleTouch = touchDays !== null && touchDays > 30;
  const lowEngage = ampScore !== null && ampScore < 30;
  const renewalUrgent = renewalDays !== null && renewalDays < 30;
  const renewalSoon   = renewalDays !== null && renewalDays < 60;

  if (renewalUrgent && (noTouch || lowEngage)) return 'critical';
  if (renewalUrgent || (noTouch && renewalSoon)) return 'high';
  if (renewalSoon || (staleTouch && lowEngage)) return 'medium';
  return 'low';
}

const RISK_COLOR = {
  critical: { bg: '#fee2e2', color: 'var(--c-red)',   label: 'Critical' },
  high:     { bg: '#fef3c7', color: 'var(--c-amber)', label: 'High'     },
  medium:   { bg: '#e0f2fe', color: '#0369a1',        label: 'Medium'   },
  low:      { bg: '#d1fae5', color: 'var(--c-green)', label: 'Low'      },
};

const RISK_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

// ── Row facet accessors (feed the column filters + sort) ─────────────────────

const custRoles    = (r) => { const v = String(r._contact.role ?? '').trim(); return v ? [v] : []; };
// Sector: the live product sector (derived by the app server from the user's
// default watchlist, IMP-1843) wins; BigDough's sector_coverage tags are the
// fallback for contacts with no product account / no dominant sector.
const custSectors  = (r) => r._contact._sector
  ? [r._contact._sector]
  : String(r._contact.sector_coverage ?? '').split(',').map((s) => s.trim()).filter(Boolean);
const custFeatures = (r) => String(r.features_used ?? '').split(',').map((s) => s.trim()).filter(Boolean);
const custUsageTier = (r) => r._ampTier ? (SCORE_TIER_LABEL[r._ampTier] ?? r._ampTier) : 'No data';

// Last product login — Clerk's last_sign_in_at, synced onto the contact by
// the customer merge (contacts.clerk_last_sign_in). Same buckets as Trial
// Management's Last login column.
const custLastLogin = (r) => _v(r._contact.clerk_last_sign_in).trim() || null;
const LOGIN_BUCKETS = ['Today', 'Last 7 days', '8–14 days', '15+ days', 'Never'];
function custLoginBucket(r) {
  const ll = custLastLogin(r);
  if (!ll) return 'Never';
  const days = Math.floor((Date.now() - new Date(ll).getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days <= 7) return 'Last 7 days';
  if (days <= 14) return '8–14 days';
  return '15+ days';
}

const EMPTY_COL_FILTERS = {
  firm: new Set(), role: new Set(), sector: new Set(),
  risk: new Set(), login: new Set(), usage: new Set(), feature: new Set(),
};

// The supported lifecycle statuses for a customer row — the Clerk model's
// enum (no flag = customer). Picking a different one flips the Clerk flag,
// the contact's stage, and the trial row via /api/trials/flag-clerk-user.
const CUSTOMER_STATUSES = [
  { value: 'customer', label: 'Customer', desc: 'Active customer — no Clerk lifecycle flag' },
  { value: 'trial',    label: 'Trial',    desc: 'Move to Trial Management as an active trial' },
  { value: 'churned',  label: 'Churned',  desc: 'No longer a customer — flags churned in Clerk' },
];

// Table columns (Trial Management pattern). `sortVal(r)` feeds the header
// sort; `filterKey` wires the header dropdown to colFilters/colOptions.
const CUSTOMER_TABLE_COLUMNS = [
  { key: 'contact', label: 'Contact',    filterKey: null,      sortVal: (r) => (r._contact.name ?? r._contact.email ?? '').toLowerCase() },
  { key: 'status',  label: 'Status',     filterKey: null,      sortable: false },
  { key: 'firm',    label: 'Firm',       filterKey: 'firm',    sortVal: (r) => (r._firm.firm_name ?? '').toLowerCase() },
  { key: 'role',    label: 'Role',       filterKey: 'role',    sortVal: (r) => (custRoles(r)[0] ?? '').toLowerCase() },
  { key: 'sector',  label: 'Sector',     filterKey: 'sector',  sortVal: (r) => (custSectors(r)[0] ?? '').toLowerCase() },
  // ARR column removed for now — firms' expected_arr was empty everywhere;
  // returns when contracts drive it (IMP-1827).
  { key: 'risk',    label: 'Churn Risk', filterKey: 'risk',    sortVal: (r) => RISK_ORDER[r._risk] ?? 4 },
  { key: 'renewal', label: 'Renewal',    filterKey: null,      sortVal: (r) => r._renewalDays ?? Infinity },
  { key: 'since',   label: 'Since',      filterKey: null,      sortVal: (r) => r._sinceDate || '9999' },
  { key: 'touch',   label: 'Last Touch', filterKey: null,      sortVal: (r) => r._touchDays ?? Infinity },
  { key: 'login',   label: 'Last Login', filterKey: 'login',   sortVal: (r) => { const ll = custLastLogin(r); return ll ? new Date(ll).getTime() : -Infinity; } },
  { key: 'usage',   label: 'Usage',      filterKey: 'usage',   sortVal: (r) => r._ampScore ?? -1 },
  // Same sort as Trial Management's Features column (count field, list fallback)
  { key: 'features', label: 'Features',  filterKey: 'feature', sortVal: (r) => trialFeatureCount(r) },
];

function exportCsv(rows) {
  const headers = ['Contact', 'Email', 'Firm', 'Fund Type', 'Role', 'Sector', 'Churn Risk', 'Days to Renewal', 'Customer Since', 'Last Touch (days)', 'Last Login', 'Amplitude Score', 'Features Used'];
  const lines = [
    headers.join(','),
    ...rows.map((r) => [
      r._contact.name ?? '',
      r._contact.email ?? '',
      r._firm.firm_name ?? '',
      r._firm.fund_type ?? '',
      r._contact.role ?? '',
      custSectors(r).join(', '),
      r._risk,
      r._renewalDays !== null ? r._renewalDays : '',
      r._sinceDate ?? '',
      r._touchDays !== null ? r._touchDays : '',
      custLastLogin(r) ?? '',
      r._ampScore ?? '',
      r.features_used ?? '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = `customers_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Sub-components ────────────────────────────────────────────────────────────

// ── Usage cell (customer row) ─────────────────────────────────────────────────
// Tier chip + one contextual line. Everything else lives in the contact timeline.

function CustomerUsageCell({ email, amplitudeScores }) {
  if (!amplitudeScores || !email) return <span style={{ color: 'var(--c-text-3)' }}>—</span>;
  const key = email.toLowerCase().trim();
  const amp = amplitudeScores[key];
  if (!amp) return <span style={{ color: 'var(--c-text-3)' }}>—</span>;

  const { score, tier, breakdown, currentMonthMsgs, avgMsgsPerMonth, lastActiveMonth } = amp;

  // Sub-line: most actionable single stat
  let subLine, subColor;
  if (currentMonthMsgs > 0) {
    const delta = avgMsgsPerMonth > 0 ? currentMonthMsgs - avgMsgsPerMonth : null;
    if (delta !== null && Math.abs(delta) >= 3) {
      subLine  = `${currentMonthMsgs} msgs · ${delta > 0 ? '↑' : '↓'} ${Math.abs(delta)} vs avg`;
      subColor = delta > 0 ? 'var(--c-green)' : 'var(--c-amber)';
    } else {
      subLine  = `${currentMonthMsgs} msgs this mo`;
      subColor = 'var(--c-text-3)';
    }
  } else if (lastActiveMonth) {
    subLine  = `Last active ${lastActiveMonth}`;
    subColor = tier === 'churned' ? 'var(--c-red)' : 'var(--c-text-3)';
  } else {
    subLine  = 'No activity';
    subColor = 'var(--c-text-3)';
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{
        display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 7px',
        borderRadius: 999, color: SCORE_TIER_COLOR[tier], background: SCORE_TIER_BG[tier],
        whiteSpace: 'nowrap', alignSelf: 'flex-start',
      }}>
        {score} — {SCORE_TIER_LABEL[tier]}
      </span>
      <span style={{ fontSize: 11, color: subColor }}>{subLine}</span>
    </div>
  );
}

function DeleteModal({ customers, busy, onConfirm, onCancel }) {
  // false → delete just the trial/customer record (existing behavior);
  // true  → full offboard (IMP-1833): contact hidden everywhere, trials
  //         deleted, product account locked, BD import won't re-add.
  const [deleteUser, setDeleteUser] = useState(false);
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div style={{
        background: 'var(--surface)', borderRadius: 'var(--radius-md)',
        padding: '24px 28px', width: 440, maxWidth: '92vw',
        maxHeight: '80vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
          Delete {customers.length} customer record{customers.length > 1 ? 's' : ''}?
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 8 }}>
            Records to delete
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {customers.map((r) => (
              <div key={r.trial_id ?? r._contact.contact_id} style={{ fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 500 }}>{r._contact.name || r._contact.email || '—'}</span>
                {r._firm.firm_name && (
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{r._firm.firm_name}</span>
                )}
                <span className="badge badge-customer" style={{ fontSize: 11 }}>Customer</span>
              </div>
            ))}
          </div>
        </div>
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13,
          padding: '10px 14px', marginBottom: 12, borderRadius: 6,
          border: '1px solid var(--border)', cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={deleteUser}
            onChange={(e) => setDeleteUser(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            <b>Delete the user{customers.length > 1 ? 's' : ''} entirely</b>
            <span style={{ display: 'block', color: 'var(--text-3)', marginTop: 2 }}>
              Removes them from every page (Customers, Contacts, Trial Management), deletes their trials, and locks their product account. BD imports will not re-add them.
            </span>
          </span>
        </label>
        <div style={{
          fontSize: 13, color: '#b91c1c', background: 'var(--red-bg)',
          borderRadius: 6, padding: '10px 14px', marginBottom: 20,
        }}>
          {deleteUser
            ? <>⚠ This deletes the selected user{customers.length > 1 ? 's' : ''} everywhere and locks their account{customers.length > 1 ? 's' : ''}.</>
            : <>⚠ This permanently deletes the selected trial record{customers.length > 1 ? 's' : ''}. Contacts with no trial will have their stage_override cleared instead.</>}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="btn btn-danger btn--sm"
            style={{ cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}
            onClick={() => onConfirm(deleteUser)}
            disabled={busy}
          >
            {busy ? 'Deleting…' : deleteUser
              ? `Delete ${customers.length} user${customers.length > 1 ? 's' : ''}`
              : `Delete ${customers.length} record${customers.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Trial-Management-style status bar pieces
function StatusBarDivider() {
  return <div style={{ width: 1, height: 16, background: 'var(--border)' }} />;
}

function StatusBarStat({ label, count, color, active, onClick }) {
  return (
    <button
      onClick={onClick ?? undefined}
      disabled={!onClick}
      style={{
        display: 'flex', alignItems: 'baseline', gap: 5, padding: '3px 6px', borderRadius: 6,
        border: 'none', background: active ? 'var(--surface-2)' : 'none', cursor: onClick ? 'pointer' : 'default',
        fontSize: 13.5, color: 'var(--text-3)',
      }}
    >
      <b style={{ fontSize: 14, color: color ?? 'var(--text)' }}>{count}</b> {label}
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Customers({ data, onOpenTimeline, getToken, onRefresh, amplitudeScores, settings = {} }) {
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [showModal,    setShowModal]    = useState(false);
  const [showUsageExport, setShowUsageExport] = useState(false); // fund-level raw usage download
  const [showUsageSummary, setShowUsageSummary] = useState(false); // fund × feature usage matrix
  const [deleteBusy,   setDeleteBusy]   = useState(false);
  const [error,        setError]        = useState(null);
  const [notice,       setNotice]       = useState(null); // transient success note (bulk actions)

  function flashNotice(msg) {
    setNotice(msg);
    setTimeout(() => setNotice((cur) => (cur === msg ? null : cur)), 5000);
  }

  // Bulk-apply rule: acting on a row that is part of a multi-selection
  // applies the action to every selected row (renewal edits, status changes).
  function bulkTargets(row) {
    return selectedIds.has(rowKey(row)) && selectedIds.size > 1 ? selectedRows : [row];
  }
  const [subTab,       setSubTab]       = useState('accounts');
  const [search,       setSearch]       = useState('');
  const [quickFilter,  setQuickFilter]  = useState(null); // renewing | at_risk | no_firm | suggested
  const [colFilters,   setColFilters]   = useState(EMPTY_COL_FILTERS);
  const [mergeRow,     setMergeRow]     = useState(null); // row whose suggested-contact merge is being confirmed
  const [statusMenu, setStatusMenu]         = useState(null); // { rowKey, contact, pos } — open status picker
  const [statusBusyKey, setStatusBusyKey]   = useState(null); // row key with a status change in flight
  // Contacts just moved out of Customer status — hidden immediately
  // (trials-page optimistic pattern) while the forced refresh confirms.
  const [movedAwayIds, setMovedAwayIds]     = useState(new Set());
  const [expandedCustomers, setExpandedCustomers] = useState(new Set()); // expanded customer detail rows
  const statusMenuRef = useRef(null);

  // The status menu is position:fixed — close on outside click, Escape, or
  // any scroll (same behavior as the column-filter popovers).
  useEffect(() => {
    if (!statusMenu) return;
    function onClick(e) { if (statusMenuRef.current && !statusMenuRef.current.contains(e.target)) setStatusMenu(null); }
    function onKey(e) { if (e.key === 'Escape') setStatusMenu(null); }
    function onScroll(e) { if (statusMenuRef.current && statusMenuRef.current.contains(e.target)) return; setStatusMenu(null); }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [statusMenu]);
  // Header sort: click cycles asc → desc → off (falls back to risk order).
  const [tableSort,    setTableSort]    = useState(null); // { col, dir } | null

  function cycleTableSort(col) {
    setTableSort((prev) => {
      if (prev?.col !== col) return { col, dir: 'asc' };
      if (prev.dir === 'asc') return { col, dir: 'desc' };
      return null;
    });
  }

  const renewalRedDays   = Number(settings.renewalRedDays   ?? 30);
  const renewalAmberDays = Number(settings.renewalAmberDays ?? 60);

  const contacts    = data?.contacts    ?? [];
  const firms       = data?.firms       ?? [];
  const trials      = data?.trials      ?? [];
  const demos       = data?.demos       ?? [];
  const outreachLog = data?.outreachLog ?? [];

  const firmMap = useMemo(() => {
    const m = {};
    for (const f of firms) { if (f.firm_id) m[f.firm_id] = f; }
    return m;
  }, [firms]);

  const customers = useMemo(() => {
    return contacts
      .filter((c) => {
        if (movedAwayIds.has(c.contact_id)) return false;
        const stage = (c.stage_override || '').trim() || computeStage(c, demos, trials, outreachLog);
        return stage.toLowerCase() === 'customer';
      })
      .map((c) => {
        const f = firmMap[c.firm_id] ?? {};

        // Find the latest converted trial for this contact (by end_date)
        const convertedTrial = trials
          .filter((t) => t.contact_id === c.contact_id && (t.status ?? '').toLowerCase() === 'converted')
          .sort((a, b) => ((b.end_date ?? '') > (a.end_date ?? '') ? 1 : -1))[0] ?? {};

        // Renewal: the explicit customer-level date on the contact (editable
        // inline in this table, like Trial Management's "Ends") wins; falls
        // back to the converted trial's end date until contracts drive this.
        const renewalDate = _v(c.renewal_date) || _v(convertedTrial.end_date) || '';
        const renewalDays = daysUntil(renewalDate);
        const touchDays   = daysSince(c._last_touch_ts);
        const amp         = amplitudeScores?.[c.email?.toLowerCase().trim()];
        const ampScore    = amp?.score ?? null;
        const risk        = churnRisk(renewalDays, touchDays, ampScore);
        // Customer since: the Clerk account's signup date (synced onto the
        // contact by the customer merge); CRM fallbacks — conversion date,
        // trial start, stage flip — cover contacts with no Clerk account.
        const sinceDate = (_v(c.clerk_created_at)
          || _v(convertedTrial.converted_date) || _v(convertedTrial.start_date)
          || _v(c.stage_changed_at)).slice(0, 10);

        return {
          ...convertedTrial,
          // Features: the trial row's synced values when there is one;
          // contacts.features_used (IMP-1850, synced for stage=customer
          // contacts) covers Clerk-merged customers with no trial.
          features_used:       _v(convertedTrial.features_used) || _v(c.features_used),
          features_used_count: _v(convertedTrial.features_used_count) || _v(c.features_used_count),
          _contact:      c,
          _firm:         f,
          _renewalDate:  renewalDate,
          _renewalDays:  renewalDays,
          _sinceDate:    sinceDate,
          _touchDays:    touchDays,
          _ampScore:     ampScore,
          _ampTier:      amp?.tier ?? null,
          _risk:         risk,
          _hasTrial:     !!convertedTrial.trial_id,
        };
      });
  }, [contacts, trials, demos, outreachLog, firmMap, amplitudeScores, movedAwayIds]);

  // ── Search / quick filters / column filters / sort ───────────────────────
  const anyColFilter = Object.values(colFilters).some((s) => s.size > 0);

  // Option lists (with counts) for the column filters, computed before column
  // filtering so unpicked values stay visible and can be added to a selection.
  const colOptions = useMemo(() => {
    const pool = customers;
    const tally = (getVals) => {
      const m = new Map();
      for (const r of pool) {
        const vals = getVals(r);
        for (const v of (vals.length ? vals : ['—'])) m.set(v, (m.get(v) ?? 0) + 1);
      }
      return [...m.entries()]
        .sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))
        .map(([value, count]) => ({ value, label: value, count }));
    };
    const featureNames = [...new Set(pool.flatMap(custFeatures))].sort();
    const featureOpts = featureNames.length === 0 ? [] : [
      { value: '_h_used', label: 'Has used', heading: true },
      ...featureNames.map((f) => ({ value: `used:${f}`, label: f, count: pool.filter((r) => custFeatures(r).includes(f)).length })),
      { value: '_h_unused', label: 'Has NOT used', heading: true },
      ...featureNames.map((f) => ({ value: `unused:${f}`, label: f, count: pool.filter((r) => !custFeatures(r).includes(f)).length })),
    ];
    return {
      firm:    tally((r) => [r._firm.firm_name].filter(Boolean)),
      role:    tally(custRoles),
      sector:  tally(custSectors),
      risk:    ['critical', 'high', 'medium', 'low'].map((k) => ({
        value: k, label: RISK_COLOR[k].label, count: pool.filter((r) => r._risk === k).length,
      })).filter((o) => o.count > 0),
      login:   LOGIN_BUCKETS.map((b) => ({ value: b, label: b, count: pool.filter((r) => custLoginBucket(r) === b).length })).filter((o) => o.count > 0),
      usage:   tally((r) => [custUsageTier(r)]),
      feature: featureOpts,
    };
  }, [customers]);

  function matchesColFilters(r) {
    const f = colFilters;
    if (f.firm.size   && !f.firm.has(r._firm.firm_name || '—')) return false;
    if (f.role.size)   { const vals = custRoles(r);   if (!(vals.length ? vals : ['—']).some((v) => f.role.has(v))) return false; }
    if (f.sector.size) { const vals = custSectors(r); if (!(vals.length ? vals : ['—']).some((v) => f.sector.has(v))) return false; }
    if (f.risk.size   && !f.risk.has(r._risk)) return false;
    if (f.login.size  && !f.login.has(custLoginBucket(r))) return false;
    if (f.usage.size  && !f.usage.has(custUsageTier(r))) return false;
    if (f.feature.size) {
      const used = new Set(custFeatures(r));
      // AND semantics: every selected used:/unused: constraint must hold.
      for (const sel of f.feature) {
        const i = sel.indexOf(':');
        const mode = sel.slice(0, i), feat = sel.slice(i + 1);
        if (mode === 'used'   && !used.has(feat)) return false;
        if (mode === 'unused' &&  used.has(feat)) return false;
      }
    }
    return true;
  }

  function matchesSearch(r) {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    // Pod shops: searching the parent firm ("millennium") also matches
    // customers at its child pods — same behavior as Trial Management.
    const parentFirm = firmMap[r._firm.parent_firm_id]?.firm_name ?? '';
    return `${r._contact.name ?? ''} ${r._contact.email ?? ''} ${r._firm.firm_name ?? ''} ${parentFirm}`.toLowerCase().includes(q);
  }

  function matchesQuickFilter(r) {
    if (!quickFilter) return true;
    if (quickFilter === 'renewing') return r._renewalDays !== null && r._renewalDays < renewalRedDays;
    if (quickFilter === 'at_risk')  return r._risk === 'critical' || r._risk === 'high';
    if (quickFilter === 'no_firm')  return !String(r._contact.firm_id ?? '').trim();
    if (quickFilter === 'suggested') return !!String(r._contact.suggested_contact_id ?? '').trim();
    return true;
  }

  const displayList = useMemo(() => {
    let list = customers.filter((r) => matchesSearch(r) && matchesQuickFilter(r) && matchesColFilters(r));
    const sortCol = tableSort && CUSTOMER_TABLE_COLUMNS.find((c) => c.key === tableSort.col);
    if (sortCol) {
      const sign = tableSort.dir === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        const va = sortCol.sortVal(a), vb = sortCol.sortVal(b);
        return (va < vb ? -1 : va > vb ? 1 : 0) * sign;
      });
    } else {
      // Default: churn risk, most urgent renewal first within a band.
      list = [...list].sort((a, b) =>
        (RISK_ORDER[a._risk] - RISK_ORDER[b._risk])
        || ((a._renewalDays ?? Infinity) - (b._renewalDays ?? Infinity)));
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, search, quickFilter, colFilters, tableSort, renewalRedDays]);

  // ── Summary stats (over all customers, not the filtered view) ────────────
  const renewingSoon = customers.filter((c) => c._renewalDays !== null && c._renewalDays < renewalRedDays).length;
  const atRisk       = customers.filter((c) => c._risk === 'critical' || c._risk === 'high').length;
  const noFirm       = customers.filter((c) => !String(c._contact.firm_id ?? '').trim()).length;
  const suggested    = customers.filter((c) => String(c._contact.suggested_contact_id ?? '').trim()).length;

  // ── Selection ────────────────────────────────────────────────────────────
  function rowKey(row) { return row.trial_id ?? row._contact.contact_id; }
  const rowKeys      = displayList.map(rowKey);
  const allSelected  = displayList.length > 0 && rowKeys.every((k) => selectedIds.has(k));
  const someSelected = !allSelected && rowKeys.some((k) => selectedIds.has(k));

  function toggleSelect(key) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds(allSelected ? new Set() : new Set(rowKeys));
  }

  const selectedRows = customers.filter((r) => selectedIds.has(rowKey(r)));

  function toggleQuickFilter(key) {
    setQuickFilter((prev) => (prev === key ? null : key));
  }

  // Status picker (replaces the old confirm-dialog "mark as trial"): pick a
  // lifecycle from the CUSTOMER_STATUSES enum — flags the user in Clerk,
  // moves the contact's stage, and upserts the trial row in one call, so the
  // row leaves this table and shows in Trial Management immediately.
  async function handleChangeStatus(row, value) {
    setStatusMenu(null);
    if (value === 'customer') return; // already the current status
    const key = rowKey(row);
    const targets = bulkTargets(row);
    setStatusBusyKey(key);
    setError(null);
    let done = 0;
    try {
      // Sequential — each call PATCHes Clerk metadata; a parallel burst over a
      // big selection risks their rate limit for no real speed win.
      for (const r of targets) {
        await apiFlagClerkUser(getToken, {
          clerk_user_id: String(r._contact.clerk_user_id ?? '').trim() || undefined,
          email: r._contact.email || undefined,
          lifecycle: value,
          contact_id: r._contact.contact_id,
        });
        setMovedAwayIds((prev) => new Set(prev).add(r._contact.contact_id));
        done++;
      }
      if (targets.length > 1) {
        flashNotice(`${done} customers moved to ${value}`);
        setSelectedIds(new Set());
      }
      onRefresh?.();
    } catch (e) {
      setError(targets.length > 1
        ? `Status change failed after ${done}/${targets.length}: ${e.message}`
        : `Status change failed: ${e.message}`);
    } finally {
      setStatusBusyKey(null);
    }
  }

  async function handleDeleteConfirmed(deleteUser = false) {
    setDeleteBusy(true);
    setError(null);
    try {
      if (deleteUser) {
        // Full offboard: hides the contact everywhere, deletes their trial
        // rows, and locks the Clerk account — one call per person.
        const contactIds = [...new Set(selectedRows.map((r) => r._contact?.contact_id).filter(Boolean))];
        await Promise.all(contactIds.map((id) => offboardContact(getToken, id)));
      } else {
        const trialIds = selectedRows.filter((r) => r._hasTrial).map((r) => r.trial_id);
        if (trialIds.length) await apiDeleteTrials(getToken, trialIds);
      }
      setSelectedIds(new Set());
      setShowModal(false);
      onRefresh?.();
    } catch (e) {
      setError('Delete failed: ' + e.message);
      setDeleteBusy(false);
    }
  }

  const subTabSwitcher = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
      {[
        { key: 'accounts',  label: '👥 Accounts' },
        { key: 'contracts', label: '📄 Contracts & Invoices' },
      ].map((t) => (
        <button
          key={t.key}
          onClick={() => setSubTab(t.key)}
          style={{
            padding: '6px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
            border: subTab === t.key ? '1px solid var(--c-primary)' : '1px solid var(--c-border)',
            background: subTab === t.key ? 'var(--c-primary-bg, #eef2ff)' : 'var(--surface)',
            color: subTab === t.key ? 'var(--c-primary)' : 'var(--c-text-2)',
            cursor: 'pointer',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  if (subTab === 'contracts') {
    return (
      <div>
        {subTabSwitcher}
        <ContractsInvoices data={data} getToken={getToken} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <span>Loading customers…</span>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div>
        {subTabSwitcher}
        <div className="empty-state">
          <div className="empty-state__icon">💰</div>
          <div className="empty-state__title">No customers yet</div>
          <div className="empty-state__sub">
            Customers appear here when a contact's stage is Customer — by converting a trial, setting Stage Override = "customer" on a contact, or automatically via the Clerk sync (product users with no trial flag).
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {subTabSwitcher}
      {error && (
        <div style={{
          background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px',
          borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16,
          border: '1px solid rgba(220,38,38,.2)',
        }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{
          background: 'var(--green-bg, #d1fae5)', color: 'var(--c-green)', padding: '10px 14px',
          borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16, fontWeight: 600,
        }}>
          ✓ {notice}
        </div>
      )}

      {/* ── Status bar: clickable counts + toolbar ────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
        padding: '8px 12px', marginBottom: 12,
      }}>
        <StatusBarStat label="customers" count={customers.length} color="var(--c-green)" active={false} onClick={null} />
        <StatusBarDivider />
        <StatusBarStat label={`renewing <${renewalRedDays}d`} count={renewingSoon} color="var(--c-red)" active={quickFilter === 'renewing'} onClick={() => toggleQuickFilter('renewing')} />
        <StatusBarDivider />
        <StatusBarStat label="at churn risk" count={atRisk} color="var(--c-amber)" active={quickFilter === 'at_risk'} onClick={() => toggleQuickFilter('at_risk')} />
        {noFirm > 0 && (
          <>
            <StatusBarDivider />
            <StatusBarStat label="no firm linked" count={noFirm} color="var(--blue)" active={quickFilter === 'no_firm'} onClick={() => toggleQuickFilter('no_firm')} />
          </>
        )}
        {suggested > 0 && (
          <>
            <StatusBarDivider />
            <StatusBarStat label="suggested merges" count={suggested} color="var(--orange)" active={quickFilter === 'suggested'} onClick={() => toggleQuickFilter('suggested')} />
          </>
        )}
        <div style={{ flex: 1 }} />
        <button className="btn btn--ghost btn--sm" onClick={() => setShowUsageSummary(true)} title="Fund × feature usage grid for a date window, with totals">Usage summary</button>
        <button className="btn btn--ghost btn--sm" onClick={() => setShowUsageExport(true)} title="Download per-event usage rows for a fund's users (sub-funds included)">Raw usage</button>
        <button className="btn btn--ghost btn--sm" onClick={() => exportCsv(displayList)}>Export CSV</button>
      </div>

      {/* ── Search + active-filter summary ─────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, firm"
          style={{ flex: '1 1 260px', maxWidth: 520, fontSize: 13, padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
        />
        {anyColFilter && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
              Column filters active — {displayList.length} match{displayList.length === 1 ? '' : 'es'}
            </span>
            <button
              onClick={() => setColFilters(EMPTY_COL_FILTERS)}
              style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ── Bulk actions ───────────────────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--orange)' }}>{selectedIds.size} selected</span>
          <button className="btn btn--ghost btn--sm" onClick={() => exportCsv(selectedRows)}>Export CSV</button>
          <button className="btn btn-danger btn--sm" onClick={() => setShowModal(true)}>Delete</button>
          <button className="btn btn--ghost btn--sm" onClick={() => setSelectedIds(new Set())}>Clear</button>
        </div>
      )}

      {/* ── Table: sort + filter live in the headers (IMP-1806 pattern) ── */}
      {displayList.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">No customers match this view</div>
          <div className="empty-state__sub">Try a different filter or search term</div>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 36, paddingLeft: 14 }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected; }}
                      onChange={toggleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  {CUSTOMER_TABLE_COLUMNS.map(({ key, label, filterKey, sortable = true }) => (
                    <th key={key} style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {sortable ? (
                          <span onClick={() => cycleTableSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                            {label} {tableSort?.col === key ? (tableSort.dir === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        ) : (
                          <span>{label}</span>
                        )}
                        {filterKey && (
                          <ColumnFilter
                            compact
                            label={label}
                            options={colOptions[filterKey]}
                            selected={colFilters[filterKey]}
                            onChange={(s) => setColFilters((p) => ({ ...p, [filterKey]: s }))}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayList.map((row) => {
                  const rd      = row._renewalDays;
                  const td      = row._touchDays;
                  const rColor  = renewalColor(rd, renewalRedDays, renewalAmberDays);
                  const tColor  = touchColor(td);
                  const risk    = RISK_COLOR[row._risk];
                  const key     = rowKey(row);
                  const isSel   = selectedIds.has(key);
                  const isExpanded = expandedCustomers.has(key);
                  // Trial-backed rows key the usage journal by trial_id;
                  // Clerk-merged customers with no trial row use contact:<id>.
                  const usageKey = row.trial_id ?? (row._contact?.contact_id ? `contact:${row._contact.contact_id}` : null);
                  return (
                    <Fragment key={key}>
                    <tr style={isSel ? { background: 'var(--blue-bg)' } : undefined}>
                      <td style={{ paddingLeft: 14, width: 36 }}>
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleSelect(key)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>

                      {/* Contact */}
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          <span className="link-cell" onClick={() => onOpenTimeline?.(row._contact)}>
                            {row._contact.name ?? '—'}
                          </span>
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--c-text-3)' }}>{row._contact.email ?? ''}</div>
                        {String(row._contact.suggested_contact_id ?? '').trim() && (
                          <button
                            className="trial-suggest-chip"
                            title="Looks like the same person under an older email — click to review and merge"
                            onClick={() => setMergeRow(row)}
                          >
                            ✦ Suggested: <b>{row._contact.suggested_contact_name || 'existing contact'}</b>
                            <span className="trial-suggest-chip__hint">merge →</span>
                          </button>
                        )}
                      </td>

                      {/* Status — click to change (enum: Customer/Trial/Churned) */}
                      <td>
                        <button
                          className="badge badge-customer"
                          title="Change status"
                          disabled={statusBusyKey === key}
                          onClick={(e) => setStatusMenu({
                            rowKey: key,
                            row,
                            pos: fixedPopupPos(e.currentTarget.getBoundingClientRect(), { height: 150, width: 260 }),
                          })}
                          style={{
                            border: 'none', cursor: statusBusyKey === key ? 'wait' : 'pointer',
                            opacity: statusBusyKey === key ? 0.6 : 1, whiteSpace: 'nowrap',
                          }}
                        >
                          {statusBusyKey === key ? 'Saving…' : 'Customer ▾'}
                        </button>
                      </td>

                      {/* Firm */}
                      <td>
                        <div style={{ fontWeight: 500 }}>{row._firm.firm_name ?? '—'}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--c-text-3)' }}>{row._firm.fund_type ?? ''}</div>
                      </td>

                      {/* Role */}
                      <td style={{ fontSize: 12.5, color: 'var(--c-text-2)', maxWidth: 140 }}>
                        {custRoles(row)[0] ?? '—'}
                      </td>

                      {/* Sector — product-derived when available (title shows the pack) */}
                      <td
                        style={{ fontSize: 12.5, color: 'var(--c-text-2)', maxWidth: 140 }}
                        title={row._contact._sector
                          ? `From their Implied watchlist${row._contact._sector_pack ? ` · pack: ${row._contact._sector_pack}` : ''}`
                          : (custSectors(row).length ? 'From BigDough coverage tags' : undefined)}
                      >
                        {custSectors(row).join(', ') || '—'}
                      </td>

                      {/* Churn Risk */}
                      <td>
                        <span style={{
                          display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px',
                          borderRadius: 999, background: risk.bg, color: risk.color,
                          whiteSpace: 'nowrap',
                        }}>
                          {risk.label}
                        </span>
                      </td>

                      {/* Renewal — double-click date to edit (any row; saved
                          onto the contact, like Trial Management's "Ends") */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          <InlineDate
                            value={_v(row._renewalDate).slice(0, 10)}
                            display={row._renewalDate ? fmt(row._renewalDate) : null}
                            style={{ fontSize: 13, fontWeight: rd !== null && rd < renewalAmberDays ? 600 : 400, color: rColor }}
                            onSave={async (v) => {
                              // Editing a selected row's renewal applies it to
                              // the whole selection (bulk renewal update).
                              const targets = bulkTargets(row).map((r) => r._contact.contact_id);
                              await Promise.all(targets.map((cid) => apiUpdateContact(getToken, cid, { renewal_date: v })));
                              if (targets.length > 1) flashNotice(`Renewal set to ${fmt(v)} for ${targets.length} customers`);
                              onRefresh?.();
                            }}
                          />
                          {rd !== null && (
                            <span style={{
                              display: 'inline-block',
                              background: rd < renewalRedDays ? '#fee2e2' : rd < renewalAmberDays ? '#fef3c7' : '#d1fae5',
                              color: rColor, borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 700, alignSelf: 'flex-start',
                            }}>
                              {rd < 0 ? `${Math.abs(rd)}d overdue` : `${rd}d`}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Customer since */}
                      <td style={{ fontSize: 12.5, color: 'var(--c-text-2)', whiteSpace: 'nowrap' }}>
                        {row._sinceDate ? fmt(row._sinceDate) : '—'}
                      </td>

                      {/* Last Touch */}
                      <td>
                        <span style={{ fontSize: 13, fontWeight: td !== null && td > 45 ? 600 : 400, color: tColor }}>
                          {fmtDaysAgo(td)}
                        </span>
                      </td>

                      {/* Last product login (Clerk) */}
                      <td>
                        {(() => {
                          const ld = daysSince(custLastLogin(row));
                          const color = ld === null ? 'var(--c-text-3)' : ld <= 7 ? 'var(--c-green)' : ld <= 14 ? 'var(--c-amber)' : 'var(--c-red)';
                          return (
                            <span style={{ fontSize: 13, color }}>
                              {ld === null ? 'Never' : fmtDaysAgo(ld)}
                            </span>
                          );
                        })()}
                      </td>

                      {/* Amplitude Usage */}
                      <td style={{ minWidth: 140 }}>
                        <CustomerUsageCell
                          email={row._contact.email}
                          amplitudeScores={amplitudeScores}
                        />
                      </td>

                      {/* Features Used — machine-owned: the nightly Amplitude
                          sync overwrites this field, so it is display-only
                          (the old inline edit silently vanished within a day). */}
                      <td style={{ fontSize: 12, color: 'var(--c-text-2)', maxWidth: 160 }}>
                        {row.features_used || '—'}
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          className="action-btn"
                          title="View timeline"
                          onClick={() => onOpenTimeline?.(row._contact)}
                        >
                          📋
                        </button>
                        {usageKey && (
                          <button
                            className="btn btn--ghost btn--sm"
                            style={{ fontSize: 11, padding: '3px 6px', marginLeft: 4, color: 'var(--text-3)' }}
                            onClick={() => setExpandedCustomers(prev => {
                              const next = new Set(prev);
                              if (next.has(key)) next.delete(key);
                              else next.add(key);
                              return next;
                            })}
                            title={isExpanded ? 'Collapse' : 'Expand usage details'}
                          >
                            {isExpanded ? '▲' : '▼'}
                          </button>
                        )}
                      </td>
                    </tr>

                    {/* Expanded detail row — Usage Journal with Heatmap */}
                    {isExpanded && usageKey && (
                      <tr>
                        <td colSpan={CUSTOMER_TABLE_COLUMNS.length + 2} style={{ padding: '12px 14px', background: 'var(--surface)', borderLeft: '3px solid var(--c-green)' }}>
                          {/* Panel renders its own Usage Heatmap / Usage Journal headers */}
                          <UsageJournalPanel
                            trial={{ trial_id: usageKey }}
                            getToken={getToken}
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <DeleteModal
          customers={selectedRows}
          busy={deleteBusy}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setShowModal(false)}
        />
      )}

      {/* Status picker — the supported-status enum for a customer row */}
      {statusMenu && (
        <div
          ref={statusMenuRef}
          style={{
            position: 'fixed', top: statusMenu.pos?.top, left: statusMenu.pos?.left, zIndex: 9000, width: 260,
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.16)', padding: 6,
          }}
        >
          {bulkTargets(statusMenu.row).length > 1 && (
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--orange)', textTransform: 'uppercase', letterSpacing: '.04em', padding: '4px 9px 2px' }}>
              Applies to {bulkTargets(statusMenu.row).length} selected
            </div>
          )}
          {CUSTOMER_STATUSES.map((opt) => {
            const current = opt.value === 'customer';
            return (
              <button
                key={opt.value}
                disabled={current}
                onClick={() => handleChangeStatus(statusMenu.row, opt.value)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '7px 9px', borderRadius: 6,
                  border: 'none', background: 'none', cursor: current ? 'default' : 'pointer',
                }}
                onMouseEnter={(e) => { if (!current) e.currentTarget.style.background = 'var(--surface-2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              >
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
                  {opt.label}{current ? ' ✓' : ''}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{opt.desc}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Job-change merge (IMP-1811): fold this Clerk-created contact into the
          suggested existing one — same dialog as Trial Management's merge. */}
      {mergeRow && (
        <MergeContactModal
          title="Merge with existing contact"
          newEmail={String(mergeRow._contact.email ?? '').toLowerCase()}
          contactId={mergeRow._contact.suggested_contact_id}
          firms={firms}
          getToken={getToken}
          allowSwap
          sourceFirmName={mergeRow._firm?.firm_name ?? ''}
          onMerge={(cid, firmId, opts) => mergeClerkContact(getToken, mergeRow._contact.contact_id, { contact_id: cid, firm_id: firmId, swap: !!opts?.swap, no_longer_customer: !!opts?.no_longer_customer })}
          onKeepSeparate={(firmId) => dismissMergeSuggestion(getToken, mergeRow._contact.contact_id, { firm_id: firmId })}
          onMerged={() => { setMergeRow(null); onRefresh?.(); }}
          onClose={() => setMergeRow(null)}
          tailNote="and this Clerk-created duplicate is removed."
        />
      )}

      {/* Fund-level raw usage download */}
      {showUsageExport && (
        <UsageExportModal
          firms={firms}
          getToken={getToken}
          onClose={() => setShowUsageExport(false)}
        />
      )}

      {/* Fund × feature usage matrix */}
      {showUsageSummary && (
        <UsageSummaryModal
          getToken={getToken}
          onClose={() => setShowUsageSummary(false)}
        />
      )}
    </div>
  );
}
